import React, { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import { addCreditCard, fetchCreditCard } from "../../features/auth/userSlice";
import { getCardType } from "../../utils/data";
import { logos } from "../../constants/data";

export default function AnimatedCardForm() {
    const { t } = useTranslation();
    const [cardNumber, setCardNumber] = useState("");
    const [cardType, setCardType] = useState("Unknown");
    const [name, setName] = useState("");
    const [expiry, setExpiry] = useState("");
    const [cvv, setCvv] = useState("");
    const [isFlipped, setIsFlipped] = useState(false);
    const dispatch = useDispatch<AppDispatch>();
    const { user } = useSelector((state: RootState) => state.user);



    const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value.replace(/\D/g, "");
        if (value.length > 16) value = value.slice(0, 16);
        const formatted = value.replace(/(.{4})/g, "$1 ").trim();
        setCardNumber(formatted);
        setCardType(getCardType(value));
    };

    const handleExpiryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value.replace(/\D/g, "");
        if (value.length > 4) value = value.slice(0, 4);
        if (value.length >= 3) {
            value = value.slice(0, 2) + "/" + value.slice(2);
        }
        setExpiry(value);
    };


    const validateCardNumber = (num: string) => {
        let arr = (num + "")
            .split("")
            .reverse()
            .map((x) => parseInt(x));
        let sum = arr.reduce((acc, val, idx) => {
            if (idx % 2) {
                val *= 2;
                if (val > 9) val -= 9;
            }
            return acc + val;
        }, 0);
        return sum % 10 === 0;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const rawNumber = cardNumber.replace(/\s/g, "");

        if (!validateCardNumber(rawNumber)) {
            return toast.error(t("errors.invalidCardNumber"));
        }
        if (!name.trim()) {
            return toast.error(t("errors.nameRequired"));
        }
        if (!/^(0[1-9]|1[0-2])\/\d{2}$/.test(expiry)) {
            return toast.error(t("errors.invalidExpiry"));
        }
        const [month, year] = expiry.split("/").map(Number);
        const currentDate = new Date();
        const expiryDate = new Date(2000 + year, month - 1);
        if (expiryDate < currentDate) {
            return toast.error(t("errors.cardExpired"));
        }
        if (cvv.length < 3 || cvv.length > 4) {
            return toast.error(t("errors.invalidCVV"));
        }
        dispatch(addCreditCard({
            cardNumber:rawNumber,
            cardHolderName: name,
            cvv,
            expiryDate:expiry,
            userId: user?.id
        })).unwrap()
            .then(() => {
                user?.id && dispatch(fetchCreditCard(user.id));
            })
    };

    return (
        <div className="flex flex-col md:flex-row items-center gap-8 p-6">
            {/* البطاقة */}
            <div
                className={`relative w-96 h-56 transition-transform duration-500 transform-style-preserve-3d ${isFlipped ? "rotate-y-180" : ""}`}
                style={{ perspective: "1000px" }}
            >
                {/* وجه البطاقة */}
                <div
                    className="absolute inset-0 bg-gradient-to-r from-[#015714] to-primary text-white rounded-xl p-6 flex flex-col justify-between shadow-xl backface-hidden overflow-hidden"
                    style={{ transform: "rotateY(0deg)" }}
                >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shine"></div>

                    <div className="flex justify-between items-center relative z-10">
                        <div className="text-lg font-bold">{t("card.bankName")}</div>
                        <img src={logos[cardType]} alt={cardType} className="w-12 h-8" />
                    </div>
                    <div className="text-2xl tracking-widest relative z-10">
                        {cardNumber || "#### #### #### ####"}
                    </div>
                    <div className="flex justify-between items-center text-sm relative z-10">
                        <div>
                            <div>{t("card.cardHolder")}</div>
                            <div className="font-semibold">{name || t("card.fullNamePlaceholder")}</div>
                        </div>
                        <div>
                            <div>{t("card.expires")}</div>
                            <div className="font-semibold">{expiry || "MM/YY"}</div>
                        </div>
                    </div>
                </div>

                {/* ظهر البطاقة */}
                <div
                    className="absolute inset-0 bg-gray-800 text-white rounded-xl p-6 flex flex-col justify-center shadow-xl backface-hidden"
                    style={{ transform: "rotateY(180deg)" }}
                >
                    <div className="bg-black h-10 w-full mb-6"></div>
                    <div className="flex justify-end">
                        <div className="bg-white text-black p-2 rounded">
                            {cvv || "***"}
                        </div>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="flex-1  space-y-4">
                <div>
                    <label className="block mb-1 font-semibold">{t("form.cardNumber")}</label>
                    <input
                        type="text"
                        value={cardNumber}
                        onChange={handleCardNumberChange}
                        placeholder="#### #### #### ####"
                        className="w-full border p-3 rounded-lg focus:ring-2 focus:ring-primary outline-none"
                    />
                </div>

                <div>
                    <label className="block mb-1 font-semibold">{t("form.cardHolderName")}</label>
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={t("form.cardHolderPlaceholder")}
                        className="w-full border p-3 rounded-lg focus:ring-2 focus:ring-primary outline-none"
                    />
                </div>

                <div className="flex gap-4">
                    <div className="flex-1">
                        <label className="block mb-1 font-semibold">{t("form.expiryDate")}</label>
                        <input
                            type="text"
                            value={expiry}
                            onChange={handleExpiryChange}
                            placeholder="MM/YY"
                            className="w-full border p-3 rounded-lg focus:ring-2 focus:ring-primary outline-none"
                        />
                    </div>
                    <div className="flex-1">
                        <label className="block mb-1 font-semibold">CVV</label>
                        <input
                            type="text"
                            value={cvv}
                            onChange={(e) => setCvv(e.target.value.replace(/\D/g, ""))}
                            onFocus={() => setIsFlipped(true)}
                            onBlur={() => setIsFlipped(false)}
                            placeholder="***"
                            maxLength={4}
                            className="w-full border p-3 rounded-lg focus:ring-2 focus:ring-primary outline-none"
                        />
                    </div>
                </div>

                <button
                    type="submit"
                    className="w-full bg-primary text-white p-3 rounded-lg font-semibold hover:bg-[#015012] transition"
                >
                    {t("form.saveCard")}
                </button>
            </form>

            <style>{`
                .rotate-y-180 { transform: rotateY(180deg); }
                .backface-hidden { backface-visibility: hidden; }
                .transform-style-preserve-3d { transform-style: preserve-3d; }
                @keyframes shine {
                    0% { transform: translateX(-100%); }
                    100% { transform: translateX(100%); }
                }
                .animate-shine {
                    animation: shine 2s infinite linear;
                }
            `}</style>
        </div>
    );
}
